import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { User } from '../models/user';
import { File } from '../models/file';
import { map } from 'rxjs/operators';
import { UserProfile } from '../models/user-profile';
import { BasketballProfile } from '../models/basketball-profile';
import { VolleyballProfile } from '../models/volleyball-profile';
import { Team } from '../models/team';
import { TeamInvite } from '../models/team-invite';
import { BasketballStatistic } from '../models/basketball-statistic';
import { VolleyballStatistic } from '../models/volleyball-statistic';
import { Game } from '../models/game';
import { GameInvite } from '../models/game-invite';
import { TeamEvent } from '../models/team-event';
import { UserAccess } from '../models/user-access';
import { UserPermission } from '../models/user-permission';
import { ConfigService } from '../services/config.service';

@Injectable({providedIn: 'root'})
export class UserApi {
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {}

  async getCurrentUser(): Promise<User> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/current/`)
      .pipe(map(data => User.toFront(data)))
      .toPromise();
  }

  async getUserById(userId: number): Promise<User> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/`)
      .pipe(map(data => User.toFront(data)))
      .toPromise();
  }

  async getAccess(userId?: number): Promise<UserAccess> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/user/${userId || 'current'}/access/`)
      .pipe(map(data => UserAccess.toFront(data)))
      .toPromise();
  }

  async updateUser(userId: number, data: any): Promise<User> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/`, User.toBack(data))
      .pipe(map(result => User.toFront(result)))
      .toPromise();
  }

  async updateUserPhoto(file: any, filename?: string): Promise<File> {
    const formData = new FormData();
    formData.append('file', file, filename);
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/current/photo/`, formData)
      .pipe(map(data => File.toFront(data)))
      .toPromise();
  }

  async changePassword(userId: number, oldPassword: string, password: string, passwordConfirm: string): Promise<any> {
    const payload = {
      old_password: oldPassword,
      password,
      password_confirm: passwordConfirm
    };
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/change_password/`, payload)
      .toPromise();
  }

  async changeEmail(userId: number, email: string, password: string): Promise<any> {
    const payload = {email, password};
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/change_email/`, payload)
      .toPromise();
  }

  async changeEmailConfirm(userId, email: string, password: string, token: number): Promise<any> {
    const payload = {email, password, token};
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/change_email_confirm/`, payload)
      .toPromise();
  }

  async getUserProfile(userId: number): Promise<UserProfile> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/profile/`)
      .pipe(map(data => UserProfile.toFront(data)))
      .toPromise();
  }

  async getBasketballProfile(userId: number): Promise<BasketballProfile> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/basketball_profile/`)
      .pipe(map(data => BasketballProfile.toFront(data)))
      .toPromise();
  }

  async getVolleyballProfile(userId: number): Promise<VolleyballProfile> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/volleyball_profile/`)
      .pipe(map(data => VolleyballProfile.toFront(data)))
      .toPromise();
  }

  async updateUserProfile(userProfile: UserProfile): Promise<UserProfile> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/user/${userProfile.userId}/profile/`, UserProfile.toBack(userProfile))
      .pipe(map(data => UserProfile.toFront(data)))
      .toPromise();
  }

  async updateUserBasketballProfile(basketballProfile: BasketballProfile): Promise<BasketballProfile> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/user/${basketballProfile.userId}/basketball_profile/`, BasketballProfile.toBack(basketballProfile))
      .pipe(map(data => BasketballProfile.toFront(data)))
      .toPromise();
  }

  async updateUserVolleyballProfile(volleyballProfile: VolleyballProfile): Promise<VolleyballProfile> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/user/${volleyballProfile.userId}/volleyball_profile/`, VolleyballProfile.toBack(volleyballProfile))
      .pipe(map(data => VolleyballProfile.toFront(data)))
      .toPromise();
  }

  async getUserPermissions(userId?: number): Promise<UserPermission> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId || 'current'}/permission/`)
      .pipe(map(data => UserPermission.toFront(data)))
      .toPromise();
  }

  async updateUserPermission(data: any): Promise<UserPermission> {
    return this.httpClient
      .patch(`${this.configService.get('apiUrl')}/api/v1/user/current/permission/`, UserPermission.toBack(data))
      .pipe(map(result => UserPermission.toFront(result)))
      .toPromise();
  }

  async getTeams(userId: number): Promise<Team[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/teams/`)
      .pipe(map(data => Team.toFront(data)))
      .toPromise();
  }

  async getInvites(): Promise<TeamInvite[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/current/invites/`)
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async acceptInvite(inviteId: number): Promise<TeamInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/current/invites/${inviteId}/accept/`, {})
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async declineInvite(inviteId: number): Promise<TeamInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/current/invites/${inviteId}/decline/`, {})
      .pipe(map(data => TeamInvite.toFront(data)))
      .toPromise();
  }

  async getStatistics(userId: number): Promise<BasketballStatistic> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/statistics/`)
      .pipe(map(data => BasketballStatistic.toFront(data)))
      .toPromise();
  }

  async getStreetballStatistics(userId: number): Promise<BasketballStatistic> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/streetball_statistics/`)
      .pipe(map(data => BasketballStatistic.toFront(data)))
      .toPromise();
  }

  async getVolleyballStatistic(userId: number): Promise<VolleyballStatistic> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/volleyball_statistics/`)
      .pipe(map(data => VolleyballStatistic.toFront(data)))
      .toPromise();
  }

  async getGames(userId: number): Promise<Game[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/${userId}/games/`)
      .pipe(map(data => Game.toFront(data)))
      .toPromise();
  }

  async getGameInvites(): Promise<GameInvite[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/current/game_invites/`)
      .pipe(map(data => GameInvite.toFront(data)))
      .toPromise();
  }

  async acceptGameInvite(inviteId: number): Promise<GameInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/current/game_invites/${inviteId}/accept/`, {})
      .pipe(map(data => GameInvite.toFront(data)))
      .toPromise();
  }

  async declineGameInvite(inviteId: number): Promise<GameInvite> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/user/current/game_invites/${inviteId}/decline/`, {})
      .pipe(map(data => GameInvite.toFront(data)))
      .toPromise();
  }

  async searchUsers(query: string): Promise<User[]> {
    const params = (new HttpParams()).set('search', query);
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/`, {params})
      .pipe(map(data => User.toFront(data)))
      .toPromise();
  }

  async setWizardShowed(wizard: string): Promise<User> {
    return this.httpClient
      .put(`${this.configService.get('apiUrl')}/api/v1/user/current/wizards/`, {wizard})
      .pipe(map(data => User.toFront(data)))
      .toPromise();
  }

  async getTeamEvents(userId: number): Promise<TeamEvent[]> {
    return this.httpClient
      .get(`${this.configService.get('apiUrl')}/api/v1/user/current/team_events/`)
      .pipe(map(data => TeamEvent.toFront(data)))
      .toPromise();
  }
}
